import React from "react";
import { Box, Chip } from "@mui/material";
import { Check, X } from "lucide-react";
import type { ActionResult } from "../../../chat/hooks/useStreamingTurn";

export interface ActionResultPillsProps {
  results: ActionResult[];
}

/**
 * Confirmation pills for mutating tool calls (bot_action_result). Gives the
 * user an explicit "did my write land?" signal — "✓ Renamed role" /
 * "✗ Emailed customer" — distinct from the generic tool-end chip in the
 * WorkingPill. Succeeded pills carry the emerald accent; failed pills the
 * error color, and their aria-label is prefixed "Failed:" so assistive tech
 * (and tests) can tell them apart from the visible label alone.
 */
export function ActionResultPills({ results }: ActionResultPillsProps) {
  if (!results.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, my: 0.5 }} aria-live="polite">
      {results.map((r, i) => {
        const ok = r.status === "succeeded";
        return (
          <Chip
            key={`${r.action}-${i}`}
            size="small"
            icon={ok ? <Check size={12} /> : <X size={12} />}
            label={r.action}
            aria-label={ok ? `Done: ${r.action}` : `Failed: ${r.action}`}
            sx={{
              bgcolor: ok ? "primary.main" : "error.main",
              color: ok ? "primary.contrastText" : "error.contrastText",
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        );
      })}
    </Box>
  );
}
